// Plain-text source for the in-app legal pages. Edit the words here; LegalDocument renders them.
// DATA_PROCESSING_TERMS_VERSION must match LegalVersions.DataProcessingTerms in the backend: bump both together
// when a change means customers should accept the terms again.

export type Block =
  | { type: 'p'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'note'; text: string }

export type Section = { heading: string; blocks: Block[] }

export type LegalDoc = {
  title: string
  subtitle?: string
  meta?: string
  intro: Block[]
  sections: Section[]
}

export const DATA_PROCESSING_TERMS_VERSION = 1

export const DATA_PROCESSING_TERMS: LegalDoc = {
  title: 'Data Processing Terms',
  subtitle: 'CertSlate (DJRS Care Services Ltd, company number 17074229)',
  meta: `Version ${DATA_PROCESSING_TERMS_VERSION} · effective 21 September 2026`,
  intro: [
    {
      type: 'p',
      text:
        'These terms are part of the agreement between DJRS Care Services Ltd, trading as CertSlate ("CertSlate", "we"), and the organisation ' +
        'that uses the CertSlate service ("the Customer"). They apply to personal data that we process on the Customer\'s behalf in providing the ' +
        'service ("Customer Personal Data") and are intended to meet Article 28 of the UK GDPR (and, where it applies, the EU GDPR).',
    },
    {
      type: 'p',
      text:
        'Where these terms conflict with other terms in our agreement about the handling of personal data, these terms apply. ' +
        'Our website privacy policy covers the personal data we handle for our own purposes (our website and the Customer\'s account with us) and is separate from these terms.',
    },
  ],
  sections: [
    {
      heading: '1. Roles',
      blocks: [
        { type: 'p', text: 'For Customer Personal Data, the Customer is the controller and CertSlate is the processor. The Customer decides why and how the data is used; we process it only to provide the service.' },
        { type: 'p', text: 'Where the California Consumer Privacy Act or a similar US state law applies, CertSlate acts as the Customer\'s service provider or processor. We do not sell or share Customer Personal Data, and we do not retain, use or disclose it except to provide the service to the Customer or as the law requires.' },
      ],
    },
    {
      heading: '2. What we will do',
      blocks: [
        {
          type: 'ul',
          items: [
            'Process Customer Personal Data only on the Customer\'s documented instructions, including about transfers: our agreement, these terms, and the Customer\'s use of the service\'s features. If UK law requires us to process the data differently, we will tell the Customer before we do, unless the law stops us. If we think an instruction breaks data protection law, we will tell the Customer immediately.',
            'Make sure everyone who can access Customer Personal Data is bound by a duty of confidentiality.',
            'Keep the data secure using the measures in Schedule 3, and keep those measures appropriate to the risk.',
            'Help the Customer respond to requests from people exercising their rights (access, correction, deletion, restriction, objection, portability). The service includes tools for this, for example removing a person\'s photo. If we receive a request about Customer Personal Data, we will pass it to the Customer promptly and not respond ourselves unless told to.',
            'Help the Customer meet its security, breach-notification, impact-assessment and regulator-consultation duties, taking into account the information we hold.',
            'Tell the Customer without undue delay, and where possible within 48 hours, after becoming aware of a personal data breach affecting Customer Personal Data, with the information we have to help them assess and report it.',
          ],
        },
      ],
    },
    {
      heading: '3. Sub-processors',
      blocks: [
        { type: 'p', text: 'The Customer authorises us to use the sub-processors listed in Schedule 2. We have a written contract with each that requires the same data protection obligations as these terms, and we remain responsible to the Customer for their performance.' },
        { type: 'p', text: 'We will tell the Customer before adding or replacing a sub-processor (by email to the account administrator and by updating Schedule 2, at least 30 days ahead where possible). If the Customer objects on reasonable data protection grounds and we cannot resolve it, the Customer may end the affected service.' },
      ],
    },
    {
      heading: '4. Where data is stored and international transfers',
      blocks: [
        { type: 'p', text: 'Customer Personal Data is stored in the United Kingdom (see Schedule 2). Some sub-processors may access data from outside the UK, for example for support or to deliver email. Where that involves a restricted transfer, we rely on a lawful transfer mechanism, such as UK adequacy regulations or the ICO\'s International Data Transfer Agreement or Addendum, and will not make such a transfer without one.' },
      ],
    },
    {
      heading: '5. Audits and information',
      blocks: [
        { type: 'p', text: 'We will give the Customer the information reasonably needed to show that we meet these terms, and allow for and contribute to audits by the Customer or a reasonable auditor it appoints. To keep this workable we ask that audits are on reasonable notice, no more than once a year (unless following a breach), and that written answers and our security summary are used first.' },
      ],
    },
    {
      heading: '6. Return and deletion',
      blocks: [
        { type: 'p', text: 'When our agreement ends we will, at the Customer\'s choice, return or delete Customer Personal Data, unless the law requires us to keep it. Deleting a tenant removes its records and stored files (including photos). Copies in our database provider\'s backups expire automatically under its restore window and are not used to bring deleted data back, except to recover from a disaster.' },
        { type: 'p', text: 'While the agreement is running, the Customer can delete individual records and photos at any time.' },
      ],
    },
    {
      heading: '7. The Customer\'s responsibilities',
      blocks: [
        {
          type: 'ul',
          items: [
            'The Customer must have a lawful basis for the personal data it puts into the service, and must give people the notices the law requires, including a privacy notice covering any identity photos.',
            'The Customer decides whether to use optional features such as identity photos and showing a photo on the public certificate-verification page, and is responsible for that decision.',
            'The Customer must not use the service to process special category data or children\'s data unless it has a proper basis and any safeguards the law requires. CertSlate does not provide facial recognition or any biometric matching, and the Customer must not use photos for that purpose.',
            'The Customer keeps the data it enters accurate and up to date, and keeps its users\' sign-in details confidential.',
            'CertSlate is not responsible for the Customer\'s decisions about whether and how to use optional features such as identity photos, or for the Customer failing to have a lawful basis or to give people the notices described above. This does not limit our own obligations as processor under these terms.',
          ],
        },
      ],
    },
    {
      heading: '8. Liability, duration and changes',
      blocks: [
        { type: 'p', text: 'Liability under these terms is subject to the limits in our agreement. These terms last as long as we process Customer Personal Data. We may update them; where a change matters to the Customer\'s rights we will ask the account administrator to accept the new version in the app.' },
      ],
    },
    {
      heading: 'Schedule 1: Details of the processing',
      blocks: [
        {
          type: 'table',
          headers: ['Item', 'Detail'],
          rows: [
            ['Subject matter and purpose', 'Providing the CertSlate service: managing clients, staff and course bookings, recording training, issuing and verifying certificates, sending related emails, and (if enabled) identity photos and identity checks.'],
            ['Duration', 'The term of our agreement, plus the deletion period in section 6.'],
            ['Nature of processing', 'Storing, organising, displaying, emailing and deleting the data; generating certificates; processing uploaded photos (cropping, resizing and removing metadata).'],
            ['Types of personal data', 'Names, email addresses, phone numbers, addresses and company names; job role; course bookings, attendance and pass or fail results; training records and certificates; login details for users; and, if the Customer enables them, identity photos and identity-check records (who checked, and when).'],
            ['People the data is about', 'The Customer\'s users, trainers and administrators; the staff and managers of the Customer\'s own clients; and members of the public who book the Customer\'s open sessions.'],
            ['Special category data', 'None intended. Photos are stored as ordinary images and are not analysed to identify anyone.'],
            ['The Customer\'s obligations and rights', 'The Customer\'s obligations are in section 7. Its rights are to give instructions, to be told of and object to new sub-processors, to have its data returned or deleted, to receive our help with individuals\' requests and incidents, and to audit us, as set out in these terms.'],
          ],
        },
      ],
    },
    {
      heading: 'Schedule 2: Sub-processors',
      blocks: [
        {
          type: 'table',
          headers: ['Sub-processor', 'What they do for us', 'Where'],
          rows: [
            ['Microsoft Azure', 'Runs the CertSlate application and stores files (certificates, photos, logos, documents).', 'United Kingdom (UK West)'],
            ['Neon', 'Managed database that holds the service\'s records.', 'United Kingdom (London)'],
            ['Cloudflare', 'Delivers the web app and its network protection. Handles traffic in transit; does not keep Customer Personal Data.', 'Global network'],
            ['Resend', 'Sends the service\'s emails (invitations, certificates, reminders, sign-in links).', 'Provider\'s email infrastructure'],
            ['Sentry', 'Application error monitoring. We configure it not to send personal data by default; an error report could still incidentally include identifiers.', 'Provider\'s infrastructure'],
          ],
        },
        { type: 'p', text: 'Services the Customer chooses to connect (Stripe for card payments, Xero for accounting) act under the Customer\'s own agreement with that provider and are not our sub-processors. Address search on venue and client forms sends the text typed to the OpenStreetMap Nominatim service.' },
      ],
    },
    {
      heading: 'Schedule 3: Security measures',
      blocks: [
        {
          type: 'ul',
          items: [
            'Encryption in transit (HTTPS only) and encryption at rest provided by our hosting and database providers.',
            'Each customer\'s data is separated by tenant in the application; users can only reach their own tenant\'s data and only the parts their role allows (administrator, trainer, client manager, attendee).',
            'Passwords are stored as salted hashes; sign-in tokens are short-lived; repeated failed sign-ins lock the account; attendee sign-in uses one-time links that expire in 15 minutes.',
            'Files are kept in a private store. Identity photos are never served from a public link: they are delivered through authenticated requests, or, on the public verification page only if the Customer has enabled it, through a rate-limited endpoint with caching and search indexing turned off.',
            'Uploaded photos are re-encoded on our servers: they are checked to be real images, cropped, resized and stripped of camera and location metadata.',
            'Trainers see attendee photos only for sessions they are assigned to, and only around the day of the session.',
            'Changes to production go through automated tests before release. Database changes are applied before new code ships and are designed to be backwards compatible.',
            'Continuous database backups from our provider for recovery; actions taken by our support staff on customer accounts are recorded in an audit log.',
            'Error monitoring is configured not to collect personal data by default.',
          ],
        },
      ],
    },
  ],
}

export const PHOTO_GUIDE: LegalDoc = {
  title: 'Identity photos: guide for customers',
  subtitle: 'Using photos on certificates and registers responsibly',
  meta: 'A practical guide, not legal advice',
  intro: [
    {
      type: 'p',
      text:
        'Identity photos let your trainers confirm that the person in front of them is the person on the register, and (if you choose) let anyone who scans a certificate\'s QR code see who it belongs to. ' +
        'Because a photo is personal data about a real person, you are the "controller" and need to be able to explain why you hold it. This guide covers what to do before you switch photos on.',
    },
  ],
  sections: [
    {
      heading: 'Before you switch photos on',
      blocks: [
        {
          type: 'ol',
          items: [
            'Read and accept the Data Processing Terms (Settings > Photos). They set out what CertSlate does for you as your processor.',
            'Write down why you need photos: for example, "to prevent someone using another person\'s certificate on safety-critical sites". This is your legitimate interest. The ICO has a free template (the legitimate interests assessment) that takes about half an hour.',
            'Decide whether the public verification display is justified. Showing a photo to anyone with the QR link is the most sensitive option. If you turn it on, complete a short data protection impact assessment (the ICO also has a free template) and mention it in your privacy notice.',
            'Update your privacy notice or policy to mention identity photos: why you keep them, who can see them, how long, and how to ask for removal. The text in Settings > Photos is shown wherever a photo is added, and appears as a public page you can link to (its address is on the same screen). The public booking page links to it automatically.',
            'Think about anyone under 18. Extra care is needed for young people. If you train apprentices aged 16 or 17, decide whether photos are appropriate for them and how you will explain it (and involve a parent or guardian if that is right for your situation).',
            'Tell your clients (the companies whose staff you train) that photos may be taken, since their managers can add photos for their own staff.',
            'Check your own terms and privacy policy. Make sure the privacy policy, and any terms you have with your clients or the people you train, cover identity photos and how you use them. CertSlate cannot tell you what these should say for your circumstances: if you are unsure, take independent advice.',
            'Tell people before a photo is taken. Anyone being photographed, or having a photo uploaded for them, should know it is happening and why, for example through spoken or written notice from the trainer at the start of the course, or through your booking and joining information. The public photo notice page linked in Settings > Photos is there for you to point people to.',
          ],
        },
      ],
    },
    {
      heading: 'Who can add or see a photo',
      blocks: [
        {
          type: 'ul',
          items: [
            'Your own administrators can add, replace and remove photos for staff and attendees.',
            'A client manager can add photos for their own company\'s staff, if you have left "Manage their team" switched on for that client, and can always see their team\'s photos.',
            'Trainers see photos of the people on sessions they are assigned to, from before the day until a week after it. They can take or replace a photo at the register but cannot remove one, and they can tick "ID checked".',
            'People who book your public sessions can add their own photo when they sign in to their attendee page.',
            'On the certificate verification page, only if you have enabled it: anyone with the certificate\'s QR code or link, and only while the certificate is current.',
          ],
        },
      ],
    },
    {
      heading: 'When someone asks about their photo',
      blocks: [
        {
          type: 'ul',
          items: [
            'To remove it, open the person\'s record and choose Remove. It is deleted straight away and disappears from the verification page.',
            'To replace it, add a new one; the old one is deleted.',
            'To let someone see their photo, you can show it to them from their record. (A download option for this is planned.)',
            'You have a month to reply to a request under UK GDPR. Keep a note of what you did.',
            'If you switch photos off, every photo is hidden immediately. You can still remove them, so you can wind down safely.',
          ],
        },
      ],
    },
    {
      heading: 'What CertSlate does and does not do',
      blocks: [
        {
          type: 'ul',
          items: [
            'Photos are stored privately and never given a permanent public link.',
            'CertSlate does not run facial recognition or store any biometric data. Trainers and verifiers compare the photo to the person by eye. Please do not use photos for automated matching.',
            'Photos are cropped, resized and stripped of camera and location data when uploaded.',
            'Keep the ID-check honest: "ID checked" should mean the trainer really compared the person with the photo, or with their ID, on the day.',
          ],
        },
      ],
    },
    {
      heading: 'Free templates and guidance',
      blocks: [
        {
          type: 'ul',
          items: [
            'ICO legitimate interests assessment template: https://ico.org.uk/media2/for-organisations/forms/2258435/gdpr-guidance-legitimate-interests-sample-lia-template.docx',
            'ICO data protection impact assessment template: https://ico.org.uk/media2/migrated/2553993/dpia-template.docx',
            'ICO guidance on lawful basis and legitimate interests: https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/lawful-basis/a-guide-to-lawful-basis/legitimate-interests/',
          ],
        },
      ],
    },
    {
      heading: 'Your responsibility',
      blocks: [
        { type: 'p', text: 'This guide is general information to help you get started. It is not legal advice, and CertSlate is not a law firm or your legal, compliance or data protection adviser. If you are unsure whether a particular use of photos is appropriate, take independent advice.' },
        { type: 'p', text: 'You are the controller of the photos you collect. You alone are responsible for deciding whether and how to use identity photos; for having a lawful basis for doing so; for giving the people concerned appropriate notice; for any assessments you need (for example a legitimate interests assessment or a data protection impact assessment); for responding to requests from individuals; and for complying with data protection and any other laws that apply to you, including in relation to children and young people.' },
        { type: 'p', text: 'CertSlate provides the identity photo feature, its settings and the default wording in the product as tools only. To the fullest extent permitted by law, and subject to the limits in our agreement with you, CertSlate is not responsible for any loss, claim, complaint or penalty arising from how you choose to use identity photos, including where notice to individuals is missing or inadequate, where you have no lawful basis, or where you rely on this guide or on the default wording. This does not limit CertSlate\'s own duties as your processor under the Data Processing Terms, or any liability that cannot be excluded or limited by law.' },
      ],
    },
  ],
}
